export interface ActivityCalendarDay {
  date: Date;
  key: string;
  count: number;
  inYear: boolean;
}

export interface ActivityCalendarMonthLabel {
  label: string;
  weekIndex: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function utcDateKey(value: string | Date): string {
  return new Date(value).toISOString().slice(0, 10);
}

export function utcYearRange(year: number): { from: string; to: string } {
  return {
    from: new Date(Date.UTC(year, 0, 1)).toISOString(),
    to: new Date(Date.UTC(year + 1, 0, 1)).toISOString(),
  };
}

export function formatUtcDate(value: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

export function buildActivityCalendar(
  year: number,
  activity: ReadonlyArray<{ date: string; count: number }>
): {
  weeks: ActivityCalendarDay[][];
  months: ActivityCalendarMonthLabel[];
} {
  const counts = new Map(
    activity.map((day) => [utcDateKey(day.date), day.count])
  );
  const first = new Date(Date.UTC(year, 0, 1));
  const last = new Date(Date.UTC(year, 11, 31));
  const start = new Date(first.getTime() - first.getUTCDay() * DAY_MS);
  const end = new Date(last.getTime() + (6 - last.getUTCDay()) * DAY_MS);
  const totalDays = Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
  const weeks: ActivityCalendarDay[][] = [];

  for (let offset = 0; offset < totalDays; offset += 1) {
    const date = new Date(start.getTime() + offset * DAY_MS);
    const weekIndex = Math.floor(offset / 7);
    const key = utcDateKey(date);
    if (!weeks[weekIndex]) weeks[weekIndex] = [];
    weeks[weekIndex].push({
      date,
      key,
      count: counts.get(key) ?? 0,
      inYear: date.getUTCFullYear() === year,
    });
  }

  const months = Array.from({ length: 12 }, (_, month) => {
    const date = new Date(Date.UTC(year, month, 1));
    return {
      label: new Intl.DateTimeFormat('en-US', {
        month: 'short',
        timeZone: 'UTC',
      }).format(date),
      weekIndex: Math.floor((date.getTime() - start.getTime()) / (7 * DAY_MS)),
    };
  });

  return { weeks, months };
}
